// ============================================================
// InstancePage — édition de la config singleton de l'instance
// ============================================================

import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api';
import type { InstanceConfig } from '@siverix/shared';
import { ErrorBox } from './OverviewPage';

export function InstancePage(): JSX.Element {
  const [cfg, setCfg] = useState<InstanceConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setCfg(await api.adminInstance());
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!cfg) return;
    setSaving(true);
    setErr(null);
    setOkMsg(null);
    try {
      const updated = await api.adminUpdateInstance(cfg);
      setCfg(updated);
      setOkMsg('Configuration enregistrée.');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div>Chargement…</div>;
  if (!cfg) return <ErrorBox message={err ?? 'Configuration introuvable.'} />;

  return (
    <div style={{ maxWidth: 640 }}>
      <h1 style={{ marginTop: 0 }}>Instance</h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
        Paramètres globaux de l'instance Siverix. Toute modification est journalisée dans l'audit.
      </p>

      <form
        onSubmit={submit}
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '10px',
          padding: '24px',
        }}
      >
        <Field label="Nom de l'instance">
          <input
            type="text"
            value={cfg.name}
            onChange={(e) => setCfg({ ...cfg, name: e.target.value })}
            required
            maxLength={120}
            style={inputStyle}
          />
        </Field>

        <Field label="URL publique">
          <input
            type="url"
            value={cfg.publicUrl}
            onChange={(e) => setCfg({ ...cfg, publicUrl: e.target.value })}
            placeholder="https://siverix.example.com"
            style={inputStyle}
          />
        </Field>

        <Field label="Inscriptions ouvertes">
          <Toggle
            checked={cfg.allowSignups}
            onChange={(v) => setCfg({ ...cfg, allowSignups: v })}
          />
          <Hint text="Si désactivé, la création de compte est interdite (sauf via invite si invitesOnly)." />
        </Field>

        <Field label="Sur invitation uniquement">
          <Toggle checked={cfg.invitesOnly} onChange={(v) => setCfg({ ...cfg, invitesOnly: v })} />
        </Field>

        <Field label="Taille max upload (octets)">
          <input
            type="number"
            min={1024}
            value={cfg.maxUploadBytes}
            onChange={(e) => setCfg({ ...cfg, maxUploadBytes: Number(e.target.value) })}
            style={inputStyle}
          />
          <Hint text={`Actuel : ${(cfg.maxUploadBytes / 1024 / 1024).toFixed(1)} Mo`} />
        </Field>

        <Field label="Mode maintenance">
          <Toggle
            checked={cfg.maintenanceMode}
            onChange={(v) => setCfg({ ...cfg, maintenanceMode: v })}
          />
          <Hint text="Bloque l'accès aux utilisateurs non-admin (lecture seule)." />
        </Field>

        {err && <ErrorBox message={err} />}
        {okMsg && (
          <div
            style={{
              marginTop: '12px',
              padding: '8px 12px',
              background: 'rgba(63,185,80,.15)',
              color: 'var(--color-success)',
              borderRadius: '6px',
              fontSize: '13px',
            }}
          >
            {okMsg}
          </div>
        )}

        <button type="submit" disabled={saving} style={primaryButtonStyle}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ marginBottom: '18px' }}>
      <div
        style={{
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          marginBottom: '6px',
          textTransform: 'uppercase',
          letterSpacing: '.04em',
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function Hint({ text }: { text: string }): JSX.Element {
  return (
    <div style={{ marginTop: 4, fontSize: '11px', color: 'var(--color-text-muted)' }}>{text}</div>
  );
}

function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 48,
        height: 24,
        borderRadius: 999,
        padding: 2,
        background: checked ? 'var(--color-accent)' : 'var(--color-border)',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: checked ? 'flex-end' : 'flex-start',
        transition: 'background .15s',
      }}
    >
      <span style={{ width: 20, height: 20, background: '#fff', borderRadius: '50%' }} />
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'inherit',
};

const primaryButtonStyle: React.CSSProperties = {
  marginTop: '8px',
  padding: '10px 18px',
  background: 'var(--color-accent)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
};
